import { createQueryKey } from '../../query/createQueryKeyFactory';
import { QueryKeyParam } from '../../query/types';
import { consumerCampaignKeys } from './campaigns/keys';
import { consumerCheckoutKeys } from './checkout/keys';
import { participantsKeys } from './participants/keys';
import { consumerPostCheckoutKeys } from './post-checkout/keys';
import { consumerStoreItemKeys } from './storeItems/keys';

const consumerCartItemKeys = (scopeId: QueryKeyParam = 'user') => {
  const key = ['store', scopeId, 'consumer', 'cartItems'] as const;

  return createQueryKey({
    all: key,
    id: (cartItemId: QueryKeyParam) =>
      createQueryKey({
        all: [...key, cartItemId] as const,
        detail: [...key, cartItemId, 'detail'] as const,
      }),
  });
};

const consumerPostCheckoutCartKeys = (scopeId: QueryKeyParam = 'user') => {
  const key = ['store', scopeId, 'consumer', 'postCheckoutCarts'] as const;

  return createQueryKey({
    all: key,
    id: (cartId: QueryKeyParam) =>
      createQueryKey({
        all: [...key, cartId] as const,
        detail: [...key, cartId, 'detail'] as const,
        receipt: [...key, cartId, 'receipt'] as const,
        redeemableTickets: createQueryKey({
          all: [...key, cartId, 'redeemableTickets'] as const,
          id: (ticketId: QueryKeyParam) =>
            createQueryKey({
              all: [...key, cartId, 'redeemableTickets', ticketId] as const,
              detail: [...key, cartId, 'redeemableTickets', ticketId, 'detail'] as const,
            }),
        }),
      }),
  });
};

const consumerEventKeys = (scopeId: QueryKeyParam = 'user') => {
  const key = ['store', scopeId, 'consumer', 'events'] as const;

  return createQueryKey({
    all: key,
    id: (eventId: QueryKeyParam) =>
      createQueryKey({
        all: [...key, eventId] as const,
        redeemableTickets: [...key, eventId, 'redeemableTickets'] as const,
      }),
  });
};

const consumerFundraiserKeys = (scopeId: QueryKeyParam = 'user') => {
  const key = ['store', scopeId, 'consumer', 'fundraisers'] as const;

  return createQueryKey({
    all: key,
    id: (fundraiserId: QueryKeyParam) =>
      createQueryKey({
        all: [...key, fundraiserId] as const,
        detail: [...key, fundraiserId, 'detail'] as const,
      }),
  });
};

const consumerSelectedCausesKeys = (scopeId: QueryKeyParam = 'user') => {
  const key = ['store', scopeId, 'consumer', 'selectedCauses'] as const;

  return createQueryKey({
    all: key,
    id: (causeId: QueryKeyParam) =>
      createQueryKey({
        all: [...key, causeId] as const,
        detail: [...key, causeId, 'detail'] as const,
      }),
  });
};

const consumerCreditedDonationsKeys = (scopeId: QueryKeyParam = 'user') => {
  const key = ['store', scopeId, 'consumer', 'creditedDonations'] as const;
  return createQueryKey({
    all: key,
    id: (participantId: QueryKeyParam) =>
      createQueryKey({
        all: [...key, participantId] as const,
      }),
  });
};

const consumerKeys = (scopeId: QueryKeyParam = 'user') => {
  const keys = ['store', scopeId, 'consumers'] as const;

  return createQueryKey({
    all: keys,
    cartItems: consumerCartItemKeys(scopeId),
    postCheckoutCarts: consumerPostCheckoutCartKeys(scopeId),
    campaigns: consumerCampaignKeys(keys),
    events: consumerEventKeys(scopeId),
    fundraisers: consumerFundraiserKeys(scopeId),
    selectedCauses: consumerSelectedCausesKeys(scopeId),
    creditedDonations: consumerCreditedDonationsKeys(scopeId),
    participants: participantsKeys(scopeId),
    items: consumerStoreItemKeys(keys),
    checkout: consumerCheckoutKeys(keys),
    postCheckout: consumerPostCheckoutKeys(keys),
  });
};

export { consumerKeys };
